import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { putAndValidate } from "~/shared/services/api/fetch-client.ts";
import { pickEndpoint } from "~/shared/services/api/pick-endpoint.ts";
import { getIsInternal } from "~/shared/utils/get-deployment-type.ts";

import type { ActiveCustomerProfile } from "../schemas/active-customer-profile-schema.ts";
import { activeCustomerProfileSchema } from "../schemas/active-customer-profile-schema.ts";
import { buildActiveCustomerProfileQueryKey } from "./use-get-active-customer-profile.ts";

/**
 * Persists the external user's active customer profile via the
 * `user-preferences/active-customer` API (or its `vendor/` variant for vendor
 * users). No-ops for internal users, who do not persist an active customer
 * profile. On success the query cache is primed with the persisted selection
 * so subsequent reads stay in sync. Failures are thrown so callers can surface
 * them in an error modal.
 *
 * The endpoint is chosen from the user's currently-resolved role via
 * `useIsVendor`. Callers in the profile-switcher UX trigger this mutation
 * after the user clicks a target customer, at which point `useIsVendor`
 * reflects the role of the customer the user is *currently* signed in as. The
 * persisted-active-customer endpoint set is per user (not per customer), so
 * that role is the correct one to use.
 */
export const useSetActiveCustomerProfile = () => {
	const queryClient = useQueryClient();
	const { isVendor } = useIsVendor();
	const url = pickEndpoint(
		{
			internal: apiRoutes.ACTIVECUSTOMERPROFILE,
			external: apiRoutes.ACTIVECUSTOMERPROFILE,
			vendor: apiRoutes.VENDORACTIVECUSTOMERPROFILE,
		},
		{ isInternal: getIsInternal(), isVendor },
	);

	return useMutation({
		mutationFn: async (
			customerProfileId: number,
		): Promise<ActiveCustomerProfile> => {
			if (getIsInternal()) {
				return { customerProfileId };
			}
			const result = await putAndValidate(
				url,
				{ customerProfileId },
				activeCustomerProfileSchema,
			);
			if (!result.success) {
				throw new Error(
					result.error || "Failed to save active customer profile.",
				);
			}
			return result.data;
		},
		onSuccess: (data) => {
			queryClient.setQueryData(
				buildActiveCustomerProfileQueryKey(isVendor),
				data,
			);
		},
	});
};
